import "react-tooltip/dist/react-tooltip.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light-border.css";

import { HocuspocusProvider } from "@hocuspocus/provider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Editor } from "@tiptap/core";
import classNames from "classnames";
import { marked } from "marked";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Y from "yjs";

import { Header, Login, Page, SyncedNodeSelect } from "@/components";
import FullPageLoader from "@/components/FullPageLoader";
import longevitySubpages from "@/components/Page/tt-data/longevity.subpages.json";
import { useQueries } from "@/hooks";

import styles from "./App.module.scss";
import { loadExtensions } from "./extensions";
import useBuddy from "./hooks/useBuddy";
import { getDefaultPage, getDefaultPageId } from "./hooks/useQueries/pages";

export default function App() {
  let { projectId = "global-graph", pageId } = useParams();
  const queryClient = useQueryClient();

  const { projects, currentProject, createProject, createPage } = useQueries();
  // const sortedPages = pages.sort((b, a) => {
  //   return new Date(a.created).getTime() - new Date(b.created).getTime();
  // });
  // // const defaultPage = sortedPages.find((p) => p.isDefault);
  // const openPage = sortedPages.find((p) => p.id == pageId);
  const navigate = useNavigate();

  const { setBuddyId } = useBuddy();

  // Store navigate for use outside React components
  useEffect(() => {
    window.coordinationNavigate = navigate;
  }, [navigate]);

  const { data: defaultPageId, isError } = useQuery({
    queryKey: ["default-page"],
    queryFn: ({ signal }) => getDefaultPageId(signal, currentProject?.id),
    enabled: Boolean(currentProject?.id),
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      console.log("Default page not found, so creating it");
      createPage({ lateralID: nanoid(), isDefault: true });
    }
  }, [isError]);

  useEffect(() => {
    console.log("currentProject", currentProject);
    if (projects.length && !currentProject) {
      console.log("Project not found, so creating it");
      createProject();
    } else if (currentProject) {
      document.title = `${currentProject?.slug} - coordination.network`;
    }
  }, [projects, currentProject]);

  // useEffect(() => {
  //   if (isFetched && ((pageId && !openPage) || !defaultPage)) {
  //     console.log("Page not found, so creating it");
  //     createPage({
  //       lateralID: pageId ? pageId : nanoid(),
  //       isDefault: pageId ? false : true,
  //     });
  //   }
  // }, [isFetched, pageId, openPage, defaultPage]);

  useEffect(() => {
    console.log("pageId", pageId);
  }, [pageId]);

  const editorRef = useRef<HTMLDivElement>(null);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    console.log(event.key, event.metaKey, event.shiftKey);
    if (event.key == "m" && event.metaKey && event.shiftKey) {
      setBuddyId("87e503e8-14ba-4e95-b31c-e626d5cb7752");
    }
    if (event.key == "," && event.metaKey && event.shiftKey) {
      setBuddyId("4ad35e6f-49fa-4a30-8283-ef63f1d65adc");
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  const [syncNodeSelectVisible, setSyncNodeSelectVisible] = useState(false);

  return (
    <Login>
      {!currentProject ? <FullPageLoader /> : <></>}
      <div className={`${styles.wrapper} App`}>
        <Header />
        <SyncedNodeSelect visible={syncNodeSelectVisible} setVisible={setSyncNodeSelectVisible} />
        <div style={{ display: "none" }}>
          <button
            onClick={async () => {
              const timer = (ms) => new Promise((res) => setTimeout(res, ms));

              const items = Object.entries(longevitySubpages); //.slice(0, 2);
              const total = items.length;
              let i = 1;
              for (const [id, pageContent] of items) {
                if (!editorRef.current) return;

                const page = createPage({
                  lateralID: id,
                  isDefault: false,
                  content: JSON.stringify(pageContent),
                });

                const roomName = `lateral-ldg-${projectId}-${id}`;
                const ydoc = new Y.Doc({ guid: roomName });
                const provider = new HocuspocusProvider({
                  url: import.meta.env.VITE_HOCUSPOCUS_URL,
                  name: roomName,
                  document: ydoc,
                });
                const extensions = loadExtensions(provider, ydoc);
                const editor = new Editor({
                  element: editorRef.current,
                  extensions,
                  editable: false,
                });
                const htmlString = marked.parse(pageContent);
                editor.commands.clearContent();
                editor.commands.clearContent();
                await editor.commands.clearContent();
                editor.commands.setContent("");
                editor.commands.setContent(htmlString);
                editor.destroy();
                console.log(`${id}`, `${i}/${total}`);
                await timer(100);
                i++;
              }
            }}
          >
            Migrate
          </button>
          <div ref={editorRef} contentEditable={false}></div>
        </div>
        <Page
          id={defaultPageId}
          isDefault={true}
          className={classNames(styles.page)}
          setSyncedNodeSelectVisible={setSyncNodeSelectVisible}
          nodePageOpen={Boolean(pageId)}
        />
        {Boolean(pageId) && (
          <Page
            id={pageId}
            key={`page-${pageId}`}
            className={classNames(styles.page, styles.nodePage)}
            setSyncedNodeSelectVisible={setSyncNodeSelectVisible}
            nodePageOpen={Boolean(pageId)}
            isNodePage
          />
        )}
      </div>
    </Login>
  );
}
